import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { Inputs } from "process/translate/edit-translations.component";
import { EditTranslationService } from "process/translate/edit-translation.service";
import { BehaviorSubject, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { AiService } from "common/ai-content/ai.service";
import { ActivatedRoute } from "@angular/router";
import { RichTextInput } from "common/form/input/types/rich-text-input";
import { TextInput } from "common/form/input/types/text-input";
import { MainService } from "core/main.service";
import DisplayDescription = stages.core.format.DisplayDescription;
import Translation = stages.process.translate.Translation;
import TranslationAttributes = stages.contentgeneratorservice.TranslationAttributes;

enum TranslationError {
	SOURCE = 0,
	TARGET = 1,
}

export interface SystemMessage {
	message: string;
	isLanguageProperty: boolean;
}

@Component({
	selector: "stages-edit-translation-single-view",
	templateUrl: "edit-translation-single-view.component.html",
	styleUrls: ["edit-translation-single-view.component.scss"],
})
export class EditTranslationSingleViewComponent implements OnInit, OnDestroy {
	private readonly DEFAULT_ERROR_MESSAGE = {
		message: "ai.widget.info.nothingtodo",
		isLanguageProperty: true,
	};

	@Input()
	identifier: string = "default";

	@Input()
	form: UntypedFormGroup = new UntypedFormGroup({});

	@Input()
	inputsByLanguage = new Map<string, Inputs>();

	@Input()
	isSourceLanguage: boolean = false;

	@Input()
	translation!: Translation;

	@Input()
	type!: string;

	@Input()
	isMobileView: boolean = false;

	@Output() readonly isLoading = new EventEmitter<boolean>();

	private destroy$ = new Subject<boolean>();

	// Params to request translation
	private workspaceId!: string | null;
	private identity!: string | null;
	private pv!: string | null;

	systemMessage$ = new BehaviorSubject<SystemMessage | undefined>(undefined);
	currentLanguage$ = new BehaviorSubject<string>("");
	currentLanguage: string = "";
	initializingLanguageTabs = true;

	loading = false;

	private mobileSourceLanguage: string = "";
	mobileMenuItems: MenuItem[] = [];

	isTranslatorConfigured$ = this.mainService.applicationContext$.pipe(
		map((applicationContext) => applicationContext.translatorConfigured),
	);

	constructor(
		private editTranslationService: EditTranslationService,
		private aiService: AiService,
		private activatedRoute: ActivatedRoute,
		private mainService: MainService,
	) {
		this.activatedRoute.paramMap.pipe(takeUntil(this.destroy$)).subscribe((paramMap) => {
			this.workspaceId = paramMap.get("workspaceId");
			this.pv = paramMap.get("processVersion");
			this.identity = paramMap.get("identity");
		});
	}

	ngOnInit(): void {
		this.editTranslationService
			.getSelectedLanguages()
			.pipe(takeUntil(this.destroy$))
			.subscribe((selectedLanguagesMap) => {
				const occupiedLanguages = getOccupiedLanguages(selectedLanguagesMap, this.identifier);
				if (this.isMobileView) this.updateMobileMenuItems(selectedLanguagesMap.values());
				const currentValue = selectedLanguagesMap.get(this.identifier);
				this.switchLanguageIfAlreadyUsed(currentValue, occupiedLanguages);
			});

		this.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe((languageValue) => {
			this.currentLanguage = languageValue;
			this.editTranslationService.updateSelectedLanguage(this.identifier, this.currentLanguage);
		});
	}

	private updateMobileMenuItems(selectedLanguagesMapValues: IterableIterator<string>): void {
		this.mobileMenuItems = [];
		for (const value of selectedLanguagesMapValues) {
			for (const language of this.translation.languages) {
				if (language.id && language.id !== value) {
					if (language.id === this.translation.languageA) {
						this.mobileMenuItems.unshift(this.createMobileMenuItem(language));
					} else {
						this.mobileMenuItems.push(this.createMobileMenuItem(language));
					}
				}
			}
		}
		this.mobileSourceLanguage = this.mobileMenuItems[0]?.id ?? "";
	}

	private createMobileMenuItem(language: stages.Option): MenuItem {
		return {
			id: language.id,
			nameParam: language.name,
			name: "translate.button.mobile",
			on: async () => {
				if (language.id) await this.generateTranslation(language.id);
			},
		};
	}

	private switchLanguageIfAlreadyUsed(currentValue: string | undefined, occupiedLanguages: string[]): void {
		if (!currentValue || occupiedLanguages.includes(currentValue)) {
			let nextAvailableLanguage = "";
			if (this.initializingLanguageTabs) {
				nextAvailableLanguage = this.getDefaultLanguage();
			} else {
				for (const language of this.translation.languages) {
					if (language.id && !occupiedLanguages.includes(language.id)) {
						nextAvailableLanguage = language.id;
						break;
					}
				}
			}
			this.editTranslationService.registerSelectedLanguage(
				this.identifier,
				nextAvailableLanguage,
				this.isSourceLanguage,
			);
			this.setCurrentLanguage(nextAvailableLanguage);
		}
	}

	private getDefaultLanguage(): string {
		let defaultLanguage = "";
		if (!this.isMobileView && this.isSourceLanguage) {
			//fullscreen left side
			defaultLanguage = this.translation.languageA;
		} else if (this.isMobileView || (!this.isMobileView && !this.isSourceLanguage)) {
			//mobile or fullscreen right side
			defaultLanguage = this.translation.languageB;
		}
		this.initializingLanguageTabs = false;
		return defaultLanguage;
	}

	async generateTranslation(mobileSourceLanguage?: string): Promise<void> {
		this.systemMessage$.next(undefined);
		const sourceLanguage =
			this.isMobileView && mobileSourceLanguage
				? mobileSourceLanguage
				: this.editTranslationService.getSourceLanguage();

		if (sourceLanguage) {
			const sourceName = getNameValueForTranslation(
				this.inputsByLanguage,
				sourceLanguage,
				this.currentLanguage,
				this.form,
				false,
			);

			const sourceDescription = getDescriptionValueForTranslation(
				this.inputsByLanguage,
				sourceLanguage,
				this.currentLanguage,
				this.form,
				false,
			);
			const sourceCustomAttributes = getCustomAttributeValuesForTranslation(
				this.translation.customRichTextAttributeIdents,
				this.inputsByLanguage,
				this.form,
				sourceLanguage,
				this.currentLanguage,
				false,
			);

			if (hasErrorsOnly(sourceName, sourceDescription, sourceCustomAttributes)) {
				this.systemMessage$.next(this.DEFAULT_ERROR_MESSAGE);
				return;
			}

			this.showLoadingSpin(true);
			const translationResult = await this.getTranslationResult(
				this.workspaceId,
				this.type,
				this.pv,
				this.identity,
				{
					name: sourceName !== TranslationError.SOURCE && sourceName !== TranslationError.TARGET ? sourceName : null,
					value:
						sourceDescription !== TranslationError.TARGET && sourceDescription !== TranslationError.SOURCE
							? sourceDescription
							: null,
					customAttributes: sourceCustomAttributes === null ? null : sourceCustomAttributes,
				},
				this.currentLanguage,
				sourceLanguage,
			);

			if (isResponseError(translationResult)) {
				this.systemMessage$.next(translationResult);
			} else {
				this.setTranslationToInputs(translationResult);
			}
			this.showLoadingSpin(false);
		}
	}

	private showLoadingSpin(showLoadingSpin: boolean): void {
		this.loading = showLoadingSpin;
		this.isLoading.next(this.loading);
	}

	async getTranslationResult(
		workspaceId: string | null,
		type: string | null,
		pv: string | null,
		identity: string | null,
		aiTranslationAttributes: TranslationAttributes,
		currentLanguage: string,
		sourceLanguage?: string,
	): Promise<SystemMessage | TranslationAttributes> {
		if (workspaceId && type && pv && identity && sourceLanguage && currentLanguage) {
			return (
				this.aiService
					.getTextTranslation(workspaceId, type, pv, identity, sourceLanguage, currentLanguage, aiTranslationAttributes)
					// eslint-disable-next-line promise/prefer-await-to-then -- Will use try catch
					.catch((errorReason) => {
						let message: SystemMessage = { message: errorReason.message, isLanguageProperty: false };
						if (errorReason.error) {
							message = errorReason.error.keys
								? {
										message: errorReason.error.keys[0],
										isLanguageProperty: true,
								  }
								: { message: errorReason.error.message, isLanguageProperty: false };
						}
						return message;
					})
			);
		}
		return this.DEFAULT_ERROR_MESSAGE;
	}

	setTranslationToInputs(translationResult: TranslationAttributes): void {
		if (translationResult) {
			if (translationResult.name) {
				const nameKey = getNameKey(this.inputsByLanguage, this.currentLanguage);
				if (nameKey) {
					this.getFormGroupByLanguage(this.currentLanguage).get(nameKey)!.setValue(translationResult.name);
				}
			}

			if (translationResult.value) {
				const descriptionKey = getDescriptionKey(this.inputsByLanguage, this.currentLanguage);
				if (descriptionKey) {
					this.updateDisplayDescriptionFormControl(descriptionKey, translationResult.value);
				}
			}

			const translatedCustomAttributes = translationResult.customAttributes;
			this.translation.customRichTextAttributeIdents.forEach((ident) => {
				const customAttributeKey = getCustomAttributeKey(this.inputsByLanguage, ident, this.currentLanguage);
				if (customAttributeKey && translatedCustomAttributes && translatedCustomAttributes[ident]) {
					this.updateDisplayDescriptionFormControl(customAttributeKey, translatedCustomAttributes[ident]);
				}
			});
			this.systemMessage$.next(undefined);
		} else {
			this.systemMessage$.next(this.DEFAULT_ERROR_MESSAGE);
		}
	}

	updateDisplayDescriptionFormControl(key: string, translation: string): void {
		const formControl = this.getFormGroupByLanguage(this.currentLanguage).get(key)!;
		const currentDescription = formControl.value as DisplayDescription;
		currentDescription.html = translation;
		formControl.setValue(currentDescription);
	}

	getFormGroupByLanguage(currentLanguage: string): UntypedFormGroup {
		return this.form.get(currentLanguage) as UntypedFormGroup;
	}

	getNameInput(currentLanguage: string): TextInput | undefined {
		if (this.inputsByLanguage?.get(currentLanguage) && !this.translation.isIndexElement) {
			return this.inputsByLanguage.get(currentLanguage)!.nameInput;
		}
		return undefined;
	}

	getCustomAttributeInput(ident: string, currentLanguage: string): RichTextInput | undefined {
		if (this.inputsByLanguage.get(currentLanguage)?.customAttributes?.get(ident)) {
			return this.inputsByLanguage.get(currentLanguage)!.customAttributes!.get(ident);
		}
		return undefined;
	}

	getDescriptionInput(currentLanguage: string): RichTextInput | undefined {
		if (this.inputsByLanguage?.get(currentLanguage)) {
			return this.inputsByLanguage.get(currentLanguage)!.descriptionInput;
		}
		return undefined;
	}

	isTabActive(tabLanguage: string): boolean {
		return this.currentLanguage === tabLanguage;
	}

	setCurrentLanguage(newLanguageId: string): void {
		this.currentLanguage$.next(newLanguageId);
	}

	getCurrentLanguageName(): string {
		for (const language of this.translation.languages) {
			if (
				(!this.isMobileView && language.id === this.currentLanguage) ||
				(this.isMobileView && language.id === this.mobileSourceLanguage)
			) {
				return language.name;
			}
		}
		return "";
	}

	hasWarning(form: UntypedFormGroup, languageId: string): boolean {
		return form.get(languageId)!.invalid && (form.get(languageId)!.dirty || form.get(languageId)!.touched);
	}

	ngOnDestroy(): void {
		this.editTranslationService.removeIdentifier(this.identifier);
		this.destroy$.next(true);
		this.destroy$.complete();
	}
}

function getOccupiedLanguages(selectedLanguagesMap: Map<string, string>, currentIdentifier: string): string[] {
	const occupiedLanguages: string[] = [];
	for (const [identifier, value] of selectedLanguagesMap) {
		if (identifier !== currentIdentifier) {
			occupiedLanguages.push(value);
		}
	}
	return occupiedLanguages;
}

function getNameKey(inputsByLanguage: Map<string, Inputs>, language: string): string | undefined {
	const formGroupByLanguage = inputsByLanguage.get(language);
	if (formGroupByLanguage?.nameInput) {
		return formGroupByLanguage.nameInput.key;
	}
	return undefined;
}

function getDescriptionKey(inputsByLanguage: Map<string, Inputs>, language: string): string | undefined {
	const formGroupByLanguage = inputsByLanguage.get(language);
	if (formGroupByLanguage) {
		return formGroupByLanguage.descriptionInput?.key;
	}
	return undefined;
}

function getCustomAttributeKey(
	inputsByLanguage: Map<string, Inputs>,
	ident: string,
	language: string,
): string | undefined {
	const formGroupByLanguage = inputsByLanguage.get(language);
	if (formGroupByLanguage) {
		return formGroupByLanguage.customAttributes?.get(ident)?.key;
	}
	return undefined;
}

function isResponseError(object: unknown): object is SystemMessage {
	const tempObject = object as never;
	return "message" in tempObject;
}

// functions for a.i. translation

function getNameValueForTranslation(
	inputsByLanguage: Map<string, Inputs>,
	sourceLanguage: string,
	targetLanguage: string,
	form: UntypedFormGroup,
	enforceTranslation: boolean,
): TranslationError | string {
	const sourceFormGroup = form.get(sourceLanguage) as UntypedFormGroup;
	const targetFormGroup = form.get(targetLanguage) as UntypedFormGroup;
	const sourceNameKey = getNameKey(inputsByLanguage, sourceLanguage);
	const targetNameKey = getNameKey(inputsByLanguage, targetLanguage);

	if (!sourceNameKey) {
		return TranslationError.SOURCE;
	}

	if (!targetNameKey) {
		return TranslationError.TARGET;
	}

	const sourceName = sourceFormGroup.get(sourceNameKey)!.value;
	const targetName = targetFormGroup.get(targetNameKey)!.value;
	return targetName === null || targetName === "" || enforceTranslation
		? sourceName !== null && sourceName !== ""
			? sourceName
			: TranslationError.SOURCE
		: TranslationError.TARGET;
}

function getDescriptionValueForTranslation(
	inputsByLanguage: Map<string, Inputs>,
	sourceLanguage: string,
	targetLanguage: string,
	form: UntypedFormGroup,
	enforceTranslation: boolean,
): TranslationError | string {
	const sourceFormGroup = form.get(sourceLanguage) as UntypedFormGroup;
	const targetFormGroup = form.get(targetLanguage) as UntypedFormGroup;
	const sourceDescriptionKey = getDescriptionKey(inputsByLanguage, sourceLanguage);
	const targetDescriptionKey = getDescriptionKey(inputsByLanguage, targetLanguage);

	if (!sourceDescriptionKey) {
		return TranslationError.SOURCE;
	}

	if (!targetDescriptionKey) {
		return TranslationError.TARGET;
	}

	const targetDescription = (targetFormGroup.get(targetDescriptionKey)!.value as DisplayDescription).html;
	const sourceDescription = (sourceFormGroup.get(sourceDescriptionKey)!.value as DisplayDescription).html;

	return targetDescription === null || targetDescription === "" || enforceTranslation
		? sourceDescription !== null && sourceDescription !== ""
			? sourceDescription
			: TranslationError.SOURCE
		: TranslationError.TARGET;
}

function getCustomAttributeValuesForTranslation(
	customAttributeIdents: string[],
	inputsByLanguage: Map<string, Inputs>,
	form: UntypedFormGroup,
	sourceLanguage: string,
	targetLanguage: string,
	enforceTranslation: boolean,
): Record<string, string> {
	const sourceFormGroup = form.get(sourceLanguage) as UntypedFormGroup;
	const targetFormGroup = form.get(targetLanguage) as UntypedFormGroup;
	let sourceCustomAttributes: Record<string, string> = {};
	customAttributeIdents.forEach((ident) => {
		const sourceCustomAttributeKey = getCustomAttributeKey(inputsByLanguage, ident, sourceLanguage);
		const targetCustomAttributeKey = getCustomAttributeKey(inputsByLanguage, ident, targetLanguage);

		if (sourceCustomAttributeKey && targetCustomAttributeKey) {
			const targetCustomAttributeValue = (targetFormGroup.get(targetCustomAttributeKey)!.value as DisplayDescription)
				.html;
			const sourceCustomAttributeValue = (sourceFormGroup.get(sourceCustomAttributeKey)!.value as DisplayDescription)
				.html;

			if ((sourceCustomAttributeValue !== "" && targetCustomAttributeValue === "") || enforceTranslation) {
				const customAttribute = {
					[ident]: sourceCustomAttributeValue,
				};
				sourceCustomAttributes = { ...customAttribute };
			}
		}
	});
	return sourceCustomAttributes;
}

function hasErrorsOnly(
	sourceName: TranslationError | string,
	sourceDescription: TranslationError | string,
	sourceCustomAttributes: Record<string, string> | null,
): boolean {
	const hasNameError = sourceName === TranslationError.SOURCE || sourceName === TranslationError.TARGET;
	const hasDescriptionError =
		sourceDescription === TranslationError.TARGET || sourceDescription === TranslationError.SOURCE;
	const hasCustomAttributesError =
		sourceCustomAttributes === null || Object.entries(sourceCustomAttributes).length === 0;
	return hasNameError && hasDescriptionError && hasCustomAttributesError;
}
